import { FC, useEffect, useMemo, useState, memo } from "react"
import { PaginationState } from "@tanstack/react-table"
import { CardSkeleton, If, ListSkeleton, Table } from "../../../ui"
import Card from "./card"
import { IntegrationCell } from "./cells"
import { ToggleView } from "../../table/toolbar"
import BrandAutocomplete from "../../brand-autocomplete"
import { useInMobileView } from "../../../hooks"
import { Integration, BBColumnDef, ViewType, Brand } from "../../../../types"

interface Props {
  integrations?: Integration[]
  brands?: Brand[]
  selectedBrandId: string
  onSelectedBrandChange: (brandId: string) => void
  isLoading: boolean
  onSelect?: (id: string) => void
  totalCount?: number
  pagination?: PaginationState
  setPagination?: React.Dispatch<React.SetStateAction<PaginationState>>
}

const IntegrationsTable: FC<Props> = memo(
  ({
    integrations = [],
    brands = [],
    selectedBrandId,
    onSelectedBrandChange,
    isLoading,
    onSelect,
    totalCount = 0,
    pagination,
    setPagination
  }) => {
    const inMobileView = useInMobileView()

    const pageCount = useMemo(() => {
      if (!pagination || !totalCount) return -1
      return Math.ceil(totalCount / pagination.pageSize)
    }, [totalCount, pagination])

    const columns: BBColumnDef<Integration>[] = useMemo(
      () => [
        {
          id: "title",
          header: () => <>Name</>,
          footer: props => props.column.id,
          cell: info => (
            <IntegrationCell data={info.row.original} onSelect={onSelect} />
          )
        }
      ],
      [onSelect]
    )
    const [viewType, setViewType] = useState<ViewType>("table")

    useEffect(() => {
      if (inMobileView) setViewType("grid")
    }, [inMobileView])

    return (
      <div className="mt-6">
        <If
          condition={isLoading}
          then={viewType === "table" ? <ListSkeleton /> : <CardSkeleton />}
          else={
            <>
              <div className="flex items-center justify-between">
                <BrandAutocomplete
                  hideLabel
                  brands={brands}
                  selected={selectedBrandId}
                  onChange={onSelectedBrandChange}
                />
                {!inMobileView && (
                  <ToggleView
                    className=""
                    viewType={viewType}
                    setViewType={setViewType}
                  />
                )}
              </div>
              <Table
                className="mt-4 -mb-6"
                data={integrations}
                columns={columns}
                viewType={viewType}
                pageSize={pagination?.pageSize || 10}
                pageCount={pageCount}
                pagination={pagination}
                setPagination={setPagination}
                rowCount={totalCount}
                searchBarVisible={false}
                renderCard={props => (
                  <Card data={props.original} onSelect={onSelect} />
                )}
                noHeader
                removeMobilePagination
              />
            </>
          }
        />
      </div>
    )
  }
)

IntegrationsTable.displayName = "IntegrationsTable"

export default IntegrationsTable
